﻿import { AppControllerBase } from './AppControllerBase';
import { ethers, JsonRpcSigner } from "ethers";
import * as gs from '@ivanlog/gs';
import { IApp } from '../IApp';
import { ChainProvider, ContractFacade } from '../contracts/abstact/ContractFacade';
import { Helper } from '../libs/Helper';

export class ChainControllerBase extends AppControllerBase
{
    public static TermMessage="Please confirm that you have read and agree to the <a href=\"https://up.docs.plusx.app/?folder=ValidatorX&article=Terms%20of%20Use\" target='_blank'>Terms of Use</a> by clicking the button below.";
    private _account:string;
    private _provider:ChainProvider;
    private _chainId:number;
    private _rpc:string;

    private _constracts:ContractFacade[]=[];
    
    private _onChangeAccount:gs.EventAsyncGS;
    private _onChangeAccountToken:gs.EventAsyncGSToken<void>;
    constructor(app: IApp, chainId:number, rpc:string)
    {
        super(app);
        this._chainId=chainId;
        this._rpc=rpc;
        this._onChangeAccountToken = new gs.EventAsyncGSToken();
        this._onChangeAccount=new gs.EventAsyncGS(this._onChangeAccountToken);
    }

    get onChangeAccount() {return this._onChangeAccount;}

    get isConnected(){return this._provider!=undefined;}

    get chainId(){return this._chainId;}
    get account(){ return this._account; }
    get provider(){return this._provider; }

    async startAsync() { await this.initWeb3Async(); }
    async getAccountAsync()
    {
        if (this._account) return this._account;
        const result = this.refreshAccountAsync();
        return result;
    }

    async getChainIdAsync():Promise<number|undefined>
    {
        try
        {
            const currentChainIdRaw = await this._requestEthWithTimeoutAsync({method: 'eth_chainId'}, 2000);
            return this._normalizeChainId(currentChainIdRaw);
        } catch (e)
        {
            return undefined;
        }
    }

    async refreshAccountAsync()
    {
        //chain validation
        const currentChainId = await this.getChainIdAsync();
        if (currentChainId !== this.chainId) {
            try {
                await this._requestEthWithTimeoutAsync({
                    method: 'wallet_switchEthereumChain',
                    params: [{ chainId: ethers.toQuantity(this.chainId) }]
                }, 3000);
            } catch (switchError) {
                this._app.gui.dialogOkShowAsync("Wrong blockchain selected. Change blockchain and refresh page.");
                return await this._selectAccountAsync(undefined, new ethers.JsonRpcProvider(this._rpc));
            }
        }
        try
        {
            const accounts = await this._requestEthWithTimeoutAsync({ method: 'eth_requestAccounts' });
            if (accounts.length == 0) return;
            return await this._selectAccountAsync(accounts[0], new ethers.BrowserProvider(globalThis.ethereum));
        } catch(e) { return; }
    }

    async tryConnectAsync()
    {
        const modalWalletError = this._app.gui.dialogOkCreate("It looks like your browser doesn't have a wallet installed. Please use a browser with a functioning MetaMask or Rabby extension","Information");
        const modalAgreement = this._app.gui.dialogOkCreate("","Information");
        modalAgreement.ctrls.lText.html = ChainControllerBase.TermMessage;
        const result = await modalAgreement.showAndWaitAsync();
        if (!result) return false;
        try
        {
            const account = await this.getAccountAsync();
            if (!account) {
                if (account!=false) modalWalletError.show(); 
                return false;
            }
            return true;
        } catch (error)
        {
            modalWalletError.show();
            console.error(error);
            return false;
        }
    }

    async disconnectAsync()
    {
        try
        {
            this._account = undefined;
            const chainProvider = new ChainProvider(new ethers.JsonRpcProvider(this._rpc));
            this._provider = chainProvider;
            await this._handleSelectAccountAsync(chainProvider);
            if (!await this.isRPCConnected())
            {
                this._provider = undefined;
                this._app.gui.dialogOkShowAsync("RPC is not accessible. Check back in 15 min or change your RPC settings and refresh page.","");
                return false;
            }
            await this._onChangeAccountToken.invoke();
            return true;
        } catch (error)
        {
            console.error(error);
            return false;
        }
    }

    public getContract(type:any, address:string){
        const provider = this._provider ?? new ChainProvider(new ethers.JsonRpcProvider(this._rpc));
        return new type(address, provider);
    }

    public addContract(contract:ContractFacade) { this._constracts.push(contract); }
    public addContracts(contracts:ContractFacade[]) { gs.array.addRange(this._constracts, contracts); }

    public async isRPCConnected() {
        if (!this._provider) return false;
        try { await this._provider.provider.getBlockNumber(); return true; } 
        catch (e) { return false; }
    }

    private async _loadAccountAsync()
    {
        //chain validation
        try
        {
        const currentChainIdRaw = await this._requestEthWithTimeoutAsync({method: 'eth_chainId'}, 1000);
        const currentChainId = this._normalizeChainId(currentChainIdRaw);
        if (currentChainId !== this._chainId) return;
        const accounts = await this._requestEthWithTimeoutAsync({ method: 'eth_accounts' }, 1000);
        if (accounts.length == 0) return;
        await this.refreshAccountAsync();
        } catch(e){ console.error(e); }
    }

    private async _selectAccountAsync(account:string, provider:ethers.BrowserProvider|ethers.JsonRpcApiProvider):Promise<string|false>
    {
        let signer:JsonRpcSigner|undefined;
        this._account=account;
        if (provider instanceof ethers.BrowserProvider) signer = await provider.getSigner();
        let chainProvider = new ChainProvider(provider, signer);
        this._provider = chainProvider;
        await this._handleSelectAccountAsync(chainProvider);
        if (!await this.isRPCConnected())
        {
            this._account=undefined;
            this._provider=undefined;
            this._app.gui.dialogOkShowAsync("RPC is not accessible. Check back in 15 min or change your RPC settings and refresh page.","");
            return false;
        }
        await this._onChangeAccountToken.invoke();
        if (!this._account) return false;
        return this._account;
    }
    protected async _handleSelectAccountAsync(chainProvider:ChainProvider){
        for(const c of this._constracts) await c.setProviderAsync(chainProvider);
    };

    async getBalanceEth():Promise<string|undefined>{
        const account = this.account;
        if (!account) return;
        try {
            const balance = await this._provider.provider.getBalance(account);
            const eth = ethers.formatEther(balance); // bigint -> "decimal"
            return eth === "0.0" ? "0" : eth;
        } catch (error) {
            console.error("getBalanceEth error:", error);
            return;
        }
    }

    async addTokenToWalletAsync(address:string, symbol:string, decimal:number)
    {
        if (!globalThis.ethereum) return false;
        const eth=globalThis.ethereum;
        try {
            // 'wasAdded' is a boolean. Like any RPC method, an error can be thrown.
            const wasAdded = await this._requestEthWithTimeoutAsync({
              method: 'wallet_watchAsset',
              params: {
                type: 'ERC20',
                options: {
                  address: address, // The address of the token.
                  symbol: symbol, // A ticker symbol or shorthand, up to 5 characters.
                  decimals: decimal, // The number of decimals in the token.
                  //image: tokenImage, // A string URL of the token logo.
                },
              },
            });
            return wasAdded;
          } catch (error) {
            console.log(error);
            return false;
          }
    }

    async initWeb3Async()
    {
        try
        {
            this._provider = new ChainProvider(new ethers.JsonRpcProvider(this._rpc));
            if ((window as any).ethereum)
            {
                (window as any).ethereum.on('accountsChanged', async (accounts) => { await this.refreshAccountAsync() });
                (window as any).ethereum.on('chainChanged', async (accounts) => { await this.refreshAccountAsync() });
                await this._loadAccountAsync();
            }
        } catch(e) { this._app.gui.dialogOkShowAsync(e); }
    }

    private _normalizeChainId(chainId:any):number|undefined
    {
        if (chainId === undefined || chainId === null) return undefined;
        if (typeof chainId === "number") return chainId;
        if (typeof chainId === "bigint") return Number(chainId);
        if (typeof chainId === "string")
        {
            if (chainId.startsWith("0x") || chainId.startsWith("0X")) return Number.parseInt(chainId, 16);
            const parsed = Number.parseInt(chainId, 10);
            if (!Number.isNaN(parsed)) return parsed;
        }
        return undefined;
    }

    async _requestEthWithTimeoutAsync(args:any, timeoutMs = 60000) {
        const eth=globalThis.ethereum;
        if (!eth || !eth.request) throw new Error("No wallet provider found");
        return Promise.race([
        eth.request(args),
        new Promise((_, reject) =>
            setTimeout(() => reject(new Error('eth.request timeout')), timeoutMs)
        ),
    ]);
}
}
